import type { ReceiptData, PrinterStatusResponse, StoreInfo } from '@abcpay/shared'
import type { ReceiptTransactionData } from '../types/receipt'
import { getReceiptStoreInfo } from './receiptSettings'
import { readPrinterSettings } from './settingsManager'
import { bridge, BridgeNotAvailableError } from '../bridge'
import { buildReceiptCommands, toBase64 } from './escpos'

// 영수증 번호 생성
function generateReceiptNumber(): string {
  const date = new Date()
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '')
  const timeStr = Date.now().toString().slice(-3)
  return `RCP-${dateStr}-${timeStr}`
}

// 거래 유형 변환
export function formatTransactionType(tradeType: 'buy' | 'sell'): string {
  return tradeType === 'buy' ? '매입 (BUYING)' : '매각 (SELLING)'
}

// 영수증 데이터 포맷팅
export function formatReceiptData(transaction: ReceiptTransactionData, storeInfo: StoreInfo): ReceiptData {
  return {
    storeInfo,
    transaction: {
      ...transaction,
      timestamp: transaction.timestamp || new Date()
    },
    receiptNumber: transaction.transactionNumber || generateReceiptNumber(),
    timestamp: new Date()
  }
}

// 프린터 사용 가능 여부
export function isPrinterAvailable(): boolean {
  return bridge.isElectron
}

// 영수증 출력 (Bridge 호출)
// 1순위: 웹에서 ESC/POS 바이트를 만들어 printer:printRaw로 전송 (포맷 변경 시 셸 업데이트 불필요)
// 폴백: printRaw를 모르는 구버전 셸이면 기존 printer:print로 셸 내장 포맷 사용
export async function printReceipt(
  transaction: ReceiptTransactionData
): Promise<PrinterStatusResponse> {
  try {
    const config = await readPrinterSettings()
    const storeInfo = getReceiptStoreInfo({ nid: transaction.branchNid, name: transaction.branchName }, config)
    const receiptData = formatReceiptData(transaction, storeInfo)

    try {
      const commands = buildReceiptCommands(receiptData, config)
      return await bridge.printReceiptRaw({ dataBase64: toBase64(commands) })
    } catch (rawError) {
      if (rawError instanceof BridgeNotAvailableError) throw rawError
      // 구버전 셸 (printRaw 미지원) → 기존 경로 폴백
      console.warn('printRaw 실패, 구버전 셸 포맷으로 폴백:', rawError)
      return await bridge.printReceipt({ receiptData })
    }
  } catch (error) {
    // Electron 외부에서 실행 시 친절한 메시지 반환
    if (error instanceof BridgeNotAvailableError) {
      return {
        success: false,
        message: '프린터 기능은 POS 앱에서만 사용 가능합니다.',
        error: 'BROWSER_MODE'
      }
    }

    return {
      success: false,
      message: '영수증 출력 중 오류 발생',
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

// 테스트 인쇄
export async function testPrinter(): Promise<PrinterStatusResponse> {
  try {
    return await bridge.testPrinter()
  } catch (error) {
    if (error instanceof BridgeNotAvailableError) {
      return {
        success: false,
        message: '프린터 기능은 POS 앱에서만 사용 가능합니다.',
        error: 'BROWSER_MODE'
      }
    }

    return {
      success: false,
      message: '테스트 인쇄 중 오류 발생',
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

// 사용 가능한 프린터 목록 조회
export async function getAvailablePrinters() {
  try {
    const result = await bridge.listPrinters()
    return result.devices
  } catch (error) {
    if (error instanceof BridgeNotAvailableError) {
      console.warn('Printer list not available in browser mode')
      return []
    }
    throw error
  }
}
